import { type FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, RefreshCw, Send } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@beamx/shared';
import { senderDomains, type SenderDomainSummary } from '../lib/api/senderDomains.js';
import { SendTestDialog } from '../components/SendTestDialog.js';

export function SenderDomainsListPage() {
  const qc = useQueryClient();
  const [domain, setDomain] = useState('');

  const list = useQuery({ queryKey: ['sender-domains'], queryFn: () => senderDomains.list() });

  const register = useMutation({
    mutationFn: (d: string) => senderDomains.register(d),
    onSuccess: () => {
      toast.success('Domain registered — publish the DNS records, then verify');
      setDomain('');
      void qc.invalidateQueries({ queryKey: ['sender-domains'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to register domain'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (domain.trim()) register.mutate(domain.trim());
  }

  return (
    <>
      <PageHeader
        title="Sender domains"
        description="Authenticate the domains you send From. Publish the generated DNS records to pass SPF, DKIM, and DMARC."
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Add a sending domain</CardTitle>
          <CardDescription>We’ll mint a DKIM key pair and return the records to publish.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex gap-2">
            <Input
              aria-label="Domain"
              placeholder="acme.example"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              required
            />
            <Button type="submit" loading={register.isPending}>Register</Button>
          </form>
        </CardContent>
      </Card>

      {list.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : list.data && list.data.length > 0 ? (
        <div className="space-y-4">
          {list.data.map((d) => <DomainCard key={d.id} domain={d} />)}
        </div>
      ) : (
        <Card>
          <CardContent className="p-6">
            <EmptyState icon={Globe} title="No sender domains" description="Register your first domain above to start sending." />
          </CardContent>
        </Card>
      )}
    </>
  );
}

function DomainCard({ domain }: { domain: SenderDomainSummary }) {
  const qc = useQueryClient();
  const [showTest, setShowTest] = useState(false);
  const verify = useMutation({
    mutationFn: () => senderDomains.verify(domain.id),
    onSuccess: (updated) => {
      const ok = updated.spfVerified && updated.dkimVerified && updated.dmarcVerified;
      if (ok) {
        toast.success(`${updated.domain} verified`);
      } else {
        toast.message('Some records are still missing', {
          description: updated.statusReason ?? 'DNS may take time to propagate.',
        });
      }
      void qc.invalidateQueries({ queryKey: ['sender-domains'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Verification failed'),
  });

  const allVerified = domain.spfVerified && domain.dkimVerified && domain.dmarcVerified;

  return (
    <>
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base">{domain.domain}</CardTitle>
          <CardDescription>
            {domain.status} · DKIM selector <span className="font-mono">{domain.dkimSelector}</span>
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <CheckBadge label="SPF" ok={domain.spfVerified} />
          <CheckBadge label="DKIM" ok={domain.dkimVerified} />
          <CheckBadge label="DMARC" ok={domain.dmarcVerified} />
          <Button size="sm" variant="outline" loading={verify.isPending} onClick={() => verify.mutate()}>
            <RefreshCw className="h-4 w-4" />
            {allVerified ? 'Re-check' : 'Verify'}
          </Button>
          {allVerified && (
            <Button size="sm" onClick={() => setShowTest(true)}>
              <Send className="h-4 w-4" />
              Send test
            </Button>
          )}
        </div>
      </CardHeader>
      {domain.requiredDnsRecords.length > 0 && (
        <CardContent>
          <details open={!allVerified}>
            <summary className="cursor-pointer text-sm font-medium text-foreground">
              Required DNS records ({domain.requiredDnsRecords.length})
            </summary>
            <div className="mt-3 rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Host</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Purpose</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domain.requiredDnsRecords.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{r.type}</TableCell>
                      <TableCell className="font-mono text-xs">{r.host}</TableCell>
                      <TableCell className="max-w-xs truncate font-mono text-xs" title={r.value}>{r.value}</TableCell>
                      <TableCell>{r.purpose}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </details>
        </CardContent>
      )}
    </Card>
    <SendTestDialog
      open={showTest}
      onOpenChange={setShowTest}
      defaultFrom={`noreply@${domain.domain}`}
      subject={`BeamX test from ${domain.domain}`}
      html="<p>This is a test email sent through BeamX.</p>"
      text="This is a test email sent through BeamX."
    />
    </>
  );
}

function CheckBadge({ label, ok }: { label: string; ok: boolean }) {
  return (
    <Badge variant={ok ? 'success' : 'warning'} dot>
      {label}
    </Badge>
  );
}
